<script>
import Config from '@/js/config';
import axios from 'axios';
import constants from '@/js/constants';
import Button from '@/components/v3/base/Button';
import ExperienceGroup from '@/components/v3/widgets/ExperienceGroup';
import DownloadIcon from '@/assets/icons/download-icon1.svg';
import WarningIcon from '@/assets/icons/warning.svg';
import CustomImage from '@/components/card/CustomImage';
import LinkedInIcon from '@/assets/icons/linkedin-icon1.svg';
import DefaultAvatar from '@/assets/img/empty_avatar.png';
import BiasAvatar1 from '@/assets/icons/candidate-photo-1.svg';
import BiasAvatar2 from '@/assets/icons/candidate-photo-2.svg';
import BiasAvatar3 from '@/assets/icons/candidate-photo-3.svg';
import BiasAvatar4 from '@/assets/icons/candidate-photo-4.svg';
import BiasAvatar5 from '@/assets/icons/candidate-photo-5.svg';
import Typography from '@/components/v3/base/Typography';
import InfoIcon from '@/assets/icons/info-icon.svg';

const BIAS_AVATARS = [
  BiasAvatar1,
  BiasAvatar2,
  BiasAvatar3,
  BiasAvatar4,
  BiasAvatar5,
];
export default {
  name: 'CandidateCard',
  components: {
    Button,
    ExperienceGroup,
    Typography,
    CustomImage,
  },

  props: {
    candidate: {
      type: Object,
      default: () => ({}),
    },

    projectId: {
      type: String,
      default: '',
    },

    notes: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      downloadLoading: false,
      loading: false,
      DownloadIcon,
      WarningIcon,
      LinkedInIcon,
      InfoIcon,
    };
  },

  computed: {
    currentProjectSettings() {
      if (!this.projectId) {
        return {};
      }
      return this.$store.getters['projects/settings/getSettingsByProjectId'](
        this.projectId
      );
    },

    isBiasReducerEnabled() {
      return this.currentProjectSettings.bias_reducer;
    },

    linkedinProfile() {
      const linkedinUrlInLinkedAccounts =
        this.candidate?.linked_accounts?.linkedin?.[0];
      const linkedinUrl =
        this.candidate?.linkedin_profile_url ?? linkedinUrlInLinkedAccounts;
      const userEmail = localStorage.getItem('email') ?? '';
      return userEmail && constants.RECRUITER_PROFILE_USERS.includes(userEmail)
        ? this.candidate?.linkedin_recruiter_url
        : linkedinUrl;
    },

    candidateName() {
      if (!this.candidate) return '';
      let name = '';
      if (this.candidate.name) {
        name = this.candidate.name;
      } else {
        name = `${this.candidate.first_name} ${this.candidate.last_name}`;
      }
      return this.getInitialsName(name);
    },

    avatarUrl() {
      if (this.isBiasReducerEnabled) return BIAS_AVATARS[0];
      if (this.loading) return DefaultAvatar;
      if (this.candidate) {
        return (
          this.candidate.picture_url ||
          this.candidate.profile_pic_url ||
          DefaultAvatar
        );
      }
      return DefaultAvatar;
    },

    candidateLocation() {
      if (this.candidate.location) return this.candidate.location;
      let location = '';
      if (this.candidate.city) location += `${this.candidate.city}, `;
      if (this.candidate.state) location += this.candidate.state;
      else location += this.candidate.country;
      return location;
    },

    candidateCurrentTitle() {
      if (!this.candidate.experiences || !this.candidate.experiences.length)
        return '';
      return `${this.candidate.experiences[0].title} at ${this.candidate.experiences[0].company}`;
    },

    user() {
      return this.$store.getters['user/getUser'];
    },

    isHireflyAdmin() {
      const email = this.user.email;
      if (
        email &&
        this.getEmailDomain(email) &&
        ['hirefly.ai', 'usetango.ai', 'hirefly-recruiting.com'].some(
          (item) => item === this.getEmailDomain(email)
        )
      ) {
        return true;
      }
      return false;
    },
  },

  watch: {
    candidate: {
      handler() {
        this.loading = true;
        setTimeout(() => {
          this.loading = false;
        }, 100);
      },

      deep: true,
    },
  },

  methods: {
    getEmailDomain(email) {
      if (!email.includes('@')) return null; // Validate the format
      return email.split('@')[1];
    },

    getCandidateResume() {
      const resumeUrl = `${Config.env.API_URL}candidates/resume?candidate_id=${this.candidate._id}`;
      this.downloadLoading = true;
      axios
        .get(resumeUrl, {
          responseType: 'blob',
        })
        .then((response) => {
          if (response.status === 200) {
            const { headers } = response;
            const blob = new Blob([response.data], {
              type: headers['content-type'],
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download =
              this.candidate.core_candidate_id || this.candidate._id;
            link.click();
          }
          this.downloadLoading = false;
        })
        .catch(() => {
          this.downloadLoading = false;
        });
    },

    getContent(recommendation) {
      if (recommendation.recommendor_content)
        return recommendation.recommendor_content.trim();
      if (recommendation.content) return recommendation.content.trim();
      return '';
    },

    getRecommendations(recommendations) {
      let str = '';
      recommendations.forEach((element) => {
        str += `${this.getContent(element)}<br/><br/>`;
      });
      return str;
    },

    getInitialsName(name) {
      if (!this.isBiasReducerEnabled) return name;
      if (!name) return '';
      const names = name.split(' ');
      const specialChars = ['.', '-', '_', '('];
      return `${names
        .map((n) => (!specialChars.includes(n.charAt(0)) ? n.charAt(0) : ''))
        .filter((item) => item !== '')
        .join('.')}.`;
    },
  },
};
</script>
<template>
  <div v-if="candidate" class="tw-p-6">
    <div
      v-if="notes && !isHireflyAdmin"
      class="tw-py-3 tw-px-4 tw-bg-yellow-50 tw-rounded-lg tw-mb-3 tw-flex tw-gap-2 tw-items-center"
    >
      <div>
        <img :src="InfoIcon" />
      </div>
      <Typography variant="subtitle-2"> Notes from Hirefly: </Typography>
      <Typography variant="body-2">
        {{ notes }}
      </Typography>
    </div>
    <div class="tw-flex tw-gap-6 tw-items-center">
      <div>
        <div class="tw-w-24 tw-h-24 tw-rounded-full tw-overflow-hidden">
          <CustomImage
            :src="avatarUrl"
            class="tw-w-full tw-h-full tw-object-cover"
            :emptySrc="require('@/assets/img/empty_avatar.png')"
          />
        </div>
      </div>
      <div>
        <div class="tw-text-xl tw-leading-7 tw-font-semibold tw-font-poppins">
          {{ candidateName }}
        </div>
        <div
          v-if="
            candidate &&
            candidate.experiences &&
            candidate.experiences.length > 0
          "
          class="tw-mt-1"
        >
          {{ candidateCurrentTitle }}
        </div>
        <div class="tw-text-gray-700 tw-text-sm tw-leading-5 tw-mt-1">
          {{ candidateLocation }}
        </div>
        <div
          v-if="
            candidate &&
            candidate.domain_contacted &&
            !!candidate.domain_contacted.contacted
          "
          class="tw-text-gray-700 tw-text-sm tw-leading-5"
        >
          <img :src="WarningIcon" />
          Status: Already Contacted
          <span class="contact-date">{{
            candidate.domain_contacted.contacted_date
          }}</span>
        </div>
        <div
          class="tw-mt-3 -tw-mb-2 -tw-ml-3 tw-flex tw-items-center tw-gap-2 tw-flex-wrap"
        >
          <div v-if="linkedinProfile" class="tw-pl-3">
            <a
              :href="linkedinProfile"
              target="_blank"
              class="hover:tw-bg-highlight tw-transition-all tw-ease-in-out !tw-no-underline tw-text-purple-600 tw-border-2 tw-border-solid tw-border-purple-600 tw-py-1 tw-px-2 tw-rounded-lg tw-flex tw-items-center tw-gap-2 tw-h-[38px]"
            >
              <img :src="LinkedInIcon" />
              <Typography variant="button-2">LinkedIn</Typography>
            </a>
          </div>

          <div v-if="candidate.resume_key">
            <Button
              variant="outlined"
              size="small"
              :loading="downloadLoading"
              @click="getCandidateResume()"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <div>
                  <img :src="DownloadIcon" />
                </div>
                <div>Resume</div>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <div>
      <ExperienceGroup
        v-if="candidate.experiences && candidate.experiences.length"
        :experiences="candidate.experiences"
        titleOfViewmore="experiences"
      />
      <ExperienceGroup
        v-if="candidate.education && candidate.education.length"
        title="Education"
        :experiences="candidate.education"
        type="education"
      />
      <ExperienceGroup
        v-if="candidate.skills && candidate.skills.length"
        title="Skills"
        :experiences="candidate.skills"
        type="skills"
        :limit="15"
      />
      <ExperienceGroup
        v-if="candidate.summary && candidate.summary.length"
        title="Self summary"
        :experiences="candidate.summary"
        type="summary"
        :limit="200"
      />
      <ExperienceGroup
        v-if="candidate.recommendations && candidate.recommendations.length"
        title="Recommendations"
        :experiences="getRecommendations(candidate.recommendations)"
        type="recommendations"
      />
    </div>
  </div>
</template>
